import { faLightbulb, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isEmpty } from 'lodash';
import { useContext, useMemo } from 'react';
import type { JSX } from 'react';

import Tooltip from '@/ui/components/Tooltip';

import { AssociateBillingRowsContext } from '../contexts/AssociateBillingRowsContext';
import type { BillingReportRow } from '../types';
import { currencyFormatter } from '../utils/currencyFormatter';

import ComparedValue from './ComparedValue';

const BillingReportLineItem = ({ row }: { row: BillingReportRow }): JSX.Element => {
  const { highlightBillingRow, linkedServiceLines, removeLinkedServiceLine } = useContext(
    AssociateBillingRowsContext
  );

  const serviceLine = useMemo(() => {
    return linkedServiceLines.find(
      (linkedServiceLine) => linkedServiceLine.billingReportRow.service_id === row.service_id
    )?.serviceLine;
  }, [linkedServiceLines, row.service_id]);

  return (
    <div key={row.service_id} className="relative flex items-center">
      <Tooltip tooltipContent="Show this row in the table below">
        <button
          className="absolute -left-8 top-1/2 -translate-y-1/2 p-1 text-gray-600 hover:text-yellow-400 transition-colors"
          onClick={() => highlightBillingRow(row)}>
          <FontAwesomeIcon icon={faLightbulb} />
        </button>
      </Tooltip>

      <div className="flex-grow grid grid-cols-5 gap-x-2 py-2 px-4 mb-2 bg-gray-50 rounded shadow border ">
        <ComparedValue
          expectedValue={serviceLine?.serviceDate?.toISOString()?.split('T')[0] || '-'}
          value={new Date(row.service_date || '').toISOString()?.split('T')[0]}
          label="Date"
        />
        <ComparedValue
          expectedValue={serviceLine?.cptCodeCode || '-'}
          value={row.cpt_code || '-'}
          label="CPT Code"
        />

        <ComparedValue
          expectedValue={
            [serviceLine?.mod1, serviceLine?.mod2, serviceLine?.mod3, serviceLine?.mod4]
              .filter((mod) => mod)
              .join(', ') || '-'
          }
          value={!isEmpty(row.modifiers) ? row.modifiers : '-'}
          label="Modifiers"
        />

        <ComparedValue
          expectedValue={serviceLine?.units || '-'}
          value={row.units || '-'}
          label="Units"
        />

        <ComparedValue
          label="Fee"
          value={row.rate ? currencyFormatter(row.rate * (row.units || 1)) : '-'}
          expectedValue={
            serviceLine?.feeInCents ? currencyFormatter(serviceLine?.feeInCents / 100) : '-'
          }
        />
      </div>
      <Tooltip tooltipContent="Remove this association">
        <button
          className="absolute -right-8 top-1/2  -translate-y-1/2 p-1 text-gray-600 hover:text-red-700 transition-colors"
          aria-label="Remove this association"
          onClick={() => removeLinkedServiceLine(row)}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </Tooltip>
    </div>
  );
};

export default BillingReportLineItem;
